import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { authApiClient } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Route = createFileRoute("/auth/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { t } = useTranslation(["auth", "common"]);
  const { apiError: showApiError } = useToast();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const forgotMutation = useMutation({
    mutationFn: async (email: string) => {
      const { error } = await authApiClient.POST("/auth/forgot-password", {
        body: { email },
      });
      if (error) throw error;
    },
    onError: (err: unknown) => {
      const code = (err as { code?: string })?.code;
      showApiError(code);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = (formData.get("email") as string).trim();

    const errors: Record<string, string> = {};
    if (!email) {
      errors.email = t("common:validation.required");
    } else if (!EMAIL_RE.test(email)) {
      errors.email = t("common:validation.invalidEmail");
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    forgotMutation.mutate(email);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
        <div>
          <h2 className="text-center text-3xl font-bold text-on-surface">
            {t("forgotPasswordTitle")}
          </h2>
          <p className="mt-2 text-center text-sm text-muted">{t("forgotPasswordSubtitle")}</p>
        </div>

        {forgotMutation.isSuccess ? (
          <div className="space-y-4">
            <div className="rounded-md bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
              {t("resetLinkSent")}
            </div>
            <Link
              to="/login"
              className="block text-center text-sm font-medium text-primary hover:text-primary-hover"
            >
              {t("backToLogin")}
            </Link>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-muted">
                {t("emailLabel")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-on-surface"
              />
              {fieldErrors.email && <p className="mt-1 text-xs text-danger">{fieldErrors.email}</p>}
            </div>

            <button
              type="submit"
              disabled={forgotMutation.isPending}
              className="w-full rounded-md bg-primary px-4 py-2 text-on-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
            >
              {forgotMutation.isPending ? t("sendingResetLink") : t("sendResetLink")}
            </button>

            <Link
              to="/login"
              className="block text-center text-sm font-medium text-primary hover:text-primary-hover"
            >
              {t("backToLogin")}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
